import { Bookmark, RefreshCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Button } from '@//components/Button';
import { Spinner } from '@//components/Spinner';
import useSearchSubscription from '@//data/subscriptions/search/useSearchSubscription';
import useUpdateSearchSubscription from '@//data/subscriptions/search/useUpdateSearchSubscription';
import { useAuth } from '@//providers/AuthProvider';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';
import { getSubscribeableQueryForVehicleSearch } from '@//utils/vehicles-search/utils';
import styles from './SearchSubscription.module.scss';

const SignInModal = lazy(() => import('@//containers/SignInModal'));
const SearchSubscriptionModal = lazy(() => import('./SearchSubscriptionModal'));

interface SearchSubscriptionButtonProps {
  rootUrl: string;
  searchSubscriptionId?: string;
  totalHits: number;
}

type ModalState = 'signIn' | 'subscribe';

const SearchSubscriptionButton = ({
  rootUrl,
  searchSubscriptionId,
  totalHits,
}: SearchSubscriptionButtonProps) => {
  const tSearchSubscription = useTranslations('components.searchSubscription.search');
  const tSignInModal = useTranslations('components.signInModal.searchSubscription');
  const { initialized } = useAuth();
  const searchParams = useVehicleSearchParams(rootUrl);
  const searchSubscriptionQuery = useSearchSubscription(searchSubscriptionId);
  const searchSubscription = searchSubscriptionQuery.data?.response;
  const updateSearchSubscriptionQuery = useUpdateSearchSubscription();
  const [modal, setModal] = useState<ModalState | undefined>(undefined);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setModal(initialized ? 'subscribe' : 'signIn');
    },
    [initialized],
  );

  const closeModal = useCallback(() => setModal(undefined), []);

  const onUpdateSearchSubscription = useCallback(() => {
    if (!searchSubscription) return;
    const query = new URLSearchParams(searchParams);
    query.delete('searchSubscriptionId');
    query.sort();

    updateSearchSubscriptionQuery.mutate({
      id: searchSubscription.id,
      body: {
        name: searchSubscription.name,
        query: query.toString(),
      },
    });
  }, [searchSubscription, searchParams, updateSearchSubscriptionQuery]);

  const searchSubscriptionIsDirty = useMemo(() => {
    if (!searchSubscription) return false;
    const tmpCurrent = new URLSearchParams(searchParams);
    tmpCurrent.sort();
    const tmpSearchSubscription = new URLSearchParams(searchSubscription.query);
    tmpSearchSubscription.set('searchSubscriptionId', searchSubscription.id);
    tmpSearchSubscription.sort();
    if (tmpCurrent.toString() !== tmpSearchSubscription.toString()) {
      return true;
    }
    return false;
  }, [searchParams, searchSubscription]);

  const isAllowedToSubscribeToQuery = useMemo(() => {
    const subscribeableQuery = getSubscribeableQueryForVehicleSearch(searchParams);

    return !!subscribeableQuery.toString();
  }, [searchParams]);

  if (!isAllowedToSubscribeToQuery) return null;

  return (
    <>
      <Suspense fallback={null}>
        <SignInModal
          isOpen={modal === 'signIn'}
          title={tSignInModal('title')}
          description={tSignInModal('description')}
          interactionType="Save search result"
          onClose={closeModal}
        />
      </Suspense>
      <Suspense fallback={null}>
        <SearchSubscriptionModal
          isOpen={modal === 'subscribe'}
          rootUrl={rootUrl}
          totalHits={totalHits}
          onClose={closeModal}
        />
      </Suspense>
      <ul className={styles.list} aria-label={tSearchSubscription('ariaLabel')}>
        {searchSubscriptionId && searchSubscriptionIsDirty && (
          <li className={styles.item}>
            <Button
              variant="secondary"
              size="md"
              noWrap
              onClick={onUpdateSearchSubscription}
              title={tSearchSubscription('updateButton.title')}
              fullWidth
            >
              <RefreshCcw aria-hidden="true" />
              {tSearchSubscription('updateButton.label')}
              {updateSearchSubscriptionQuery.isPending && <Spinner size="text" />}
            </Button>
          </li>
        )}
        <li className={styles.item}>
          <Button
            variant="secondary"
            size="md"
            noWrap
            onClick={handleClick}
            title={tSearchSubscription('createButton.title')}
            fullWidth
            testId="create-subscription-button"
          >
            <Bookmark aria-hidden="true" />
            {tSearchSubscription('createButton.label')}
          </Button>
        </li>
      </ul>
    </>
  );
};

export default SearchSubscriptionButton;
